import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, Clock, AlertTriangle } from 'lucide-react';
import CustomCursor from '../components/CustomCursor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CY = '#90E0EF';
function money(minor) {
  const v = (Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `£${v}`;
}

export default function CardSuccessPage() {
  const [params] = useSearchParams();
  const code = params.get('code');
  const [sale, setSale] = useState(null);
  const [tries, setTries] = useState(0);
  const status = sale?.status;
  const done = status === 'paid' || status === 'payment_conflict';

  // Poll the status endpoint (which itself finalises against Stripe) — never
  // trust the redirect alone as proof of payment.
  useEffect(() => {
    if (!code) return;
    let stop = false;
    let n = 0;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/public/sales/${code}/status`);
        if (stop) return;
        setSale(data);
        if (data.status === 'paid' || data.status === 'payment_conflict') return;
      } catch { /* keep trying */ }
      n += 1; setTries(n);
      if (!stop && n < 14) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
  }, [code]);

  return (
    <div className="min-h-[100svh] bg-[#050505] text-[#f0f0f0] relative overflow-hidden" data-testid="card-success-page">
      <CustomCursor />
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(#90E0EF 1px, transparent 1px), linear-gradient(90deg, #90E0EF 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
      <div className="relative z-10 max-w-md mx-auto px-6 py-16 text-center">
        <div className="flex items-center justify-center gap-2.5 mb-10">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: CY, boxShadow: `0 0 8px ${CY}` }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CY }}>Mr. Relative</span>
        </div>

        {status === 'paid' && (
          <div data-testid="card-success-confirmed">
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ border: `1.5px solid ${CY}`, background: 'rgba(144,224,239,0.1)' }}>
              <Check className="w-7 h-7" style={{ color: CY }} />
            </div>
            <h1 className="font-outfit font-bold text-white tracking-tight" style={{ fontSize: '2rem' }}>You got the cards</h1>
            <div className="mt-8 border-t border-b border-[#1a1a1a] py-6">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#666]">{(sale.cards || []).length} Card Edition{(sale.cards || []).length === 1 ? '' : 's'}</p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {(sale.cards || []).map((c) => (
                  <span key={c} className="font-mono text-sm px-2.5 py-1 border" style={{ color: CY, borderColor: 'rgba(144,224,239,0.4)' }}>#{c}</span>
                ))}
              </div>
              <p className="font-outfit font-bold text-white mt-6" style={{ fontSize: '2.2rem', lineHeight: 1 }}>{money(sale.amount_minor)}</p>
              {sale.buyer_name && <p className="text-xs font-mono text-[#666] mt-3">{sale.buyer_name}</p>}
            </div>
            <p className="text-xs font-mono text-[#888] mt-6 leading-relaxed">These editions are now registered to you. The gallery will be in touch shortly.</p>
          </div>
        )}

        {status === 'payment_conflict' && (
          <div data-testid="card-success-conflict">
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ border: '1.5px solid rgba(228,192,122,0.5)', background: 'rgba(228,192,122,0.08)' }}>
              <AlertTriangle className="w-7 h-7" style={{ color: '#e4c07a' }} />
            </div>
            <h1 className="font-outfit font-semibold text-white tracking-tight" style={{ fontSize: '1.6rem' }}>We need to sort something out</h1>
            <p className="text-xs font-mono text-[#aaa] mt-5 leading-relaxed">
              Your payment went through, but one or more of these editions was claimed by another collector moments before yours completed.
              Our team will contact you right away to reassign or fully refund you — you will not lose out.
            </p>
            <p className="text-[10px] font-mono text-[#666] mt-6">Reference · {sale.public_code}</p>
          </div>
        )}

        {!done && (
          <div data-testid="card-success-pending">
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ border: '1.5px solid #333' }}>
              {tries < 14 ? <Loader2 className="w-6 h-6 animate-spin" style={{ color: CY }} /> : <Clock className="w-6 h-6 text-[#888]" />}
            </div>
            <h1 className="font-outfit font-semibold text-white tracking-tight" style={{ fontSize: '1.6rem' }}>
              {tries < 14 ? 'Confirming your payment…' : 'Almost there'}
            </h1>
            <p className="text-xs font-mono text-[#888] mt-4 leading-relaxed">
              {tries < 14
                ? 'We are verifying your payment and securing your cards. This takes a few seconds.'
                : 'Your payment is processing. You can safely close this page — the gallery will confirm your cards.'}
            </p>
          </div>
        )}

        <div className="mt-10">
          <Link to="/" className="text-[11px] font-mono text-[#666] hover:text-white transition-colors">← The Relative Gallery</Link>
        </div>
      </div>
    </div>
  );
}
